import { jsPDF } from "jspdf";

const brl = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const dataBRfromISO = (d) => {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const dataBR = (dt) =>
  `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;

const REAJUSTE = 0.08; // 8% a cada 12 meses

// Gera os ciclos de 12 meses. O valorBase informado é o valor ATUAL (já reajustado):
// o ciclo vigente usa exatamente esse valor, ciclos passados são de-escalados (÷1,08)
// e ciclos futuros recebem +8% por aniversário.
export function calcularRenovacoes(dataInicio, valorBase, ciclos = null) {
  if (!dataInicio || !valorBase) return [];
  const start = new Date(dataInicio + "T00:00:00");
  const hoje = new Date();
  // índice do ciclo vigente (0 = primeiro ano do contrato), pelo MÊS do aniversário —
  // mesma regra do reajuste automático e da mensalidade vigente nas faturas.
  let cicloAtual = Math.floor(((hoje.getFullYear() * 12 + hoje.getMonth()) - (start.getFullYear() * 12 + start.getMonth())) / 12);
  if (cicloAtual < 0) cicloAtual = 0;
  // sempre mostra todos os ciclos até o vigente + o próximo (mín. 6), sem teto de 5 anos
  const total = ciclos || Math.max(6, cicloAtual + 2);
  const list = [];
  for (let i = 0; i < total; i++) {
    const inicio = new Date(start);
    inicio.setFullYear(start.getFullYear() + i);
    const fim = new Date(start);
    fim.setFullYear(start.getFullYear() + i + 1);
    fim.setDate(fim.getDate() - 1);
    const valor = +(valorBase * Math.pow(1 + REAJUSTE, i - cicloAtual)).toFixed(2);
    list.push({
      ciclo: i + 1,
      inicio,
      fim,
      valor,
      vigente: i === cicloAtual,
      futuro: i > cicloAtual,
      temReajuste: i > cicloAtual,
    });
  }
  return list;
}

export function contratoResumo(dataInicio, valorBase) {
  const rs = calcularRenovacoes(dataInicio, valorBase, 30);
  if (rs.length === 0) return null;
  const atual = rs.find((r) => r.vigente) || rs[0];
  const proxima = rs.find((r) => r.futuro);
  return { atual, proxima, dataBR };
}

export function getResponsavelFinanceiro(residente) {
  const fams = residente.familiares || [];
  return (
    fams.find((f) => f.responsavelFinanceiro) ||
    fams[0] || {
      nome: residente.responsavel || "",
      cpf: residente.responsavelCpf || "",
      rg: residente.responsavelRg || "",
      telefone: residente.telefone || "",
      endLogradouro: residente.endLogradouro || "",
      endNumero: residente.endNumero || "",
      endBairro: residente.endBairro || "",
      endCidade: residente.endCidade || "",
      endUf: residente.endUf || "",
      endCep: residente.endCep || "",
    }
  );
}

export { dataBR, dataBRfromISO };

// -------- PDF do Contrato de Prestação de Serviços --------
export function gerarContratoPDF({ empresa, residente }) {
  const doc = new jsPDF();
  const contratante = getResponsavelFinanceiro(residente);
  const resumo = contratoResumo(residente.dataInicioContrato, residente.valorMensalidade);
  const valorAtual = resumo ? resumo.atual.valor : residente.valorMensalidade;

  const marginX = 18;
  let y = 20;
  const lineW = 174;

  // Timbrado: logo + nome no topo
  if (empresa?.logo) {
    try {
      doc.addImage(empresa.logo, "PNG", marginX, 10, 16, 16);
      doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(30, 41, 59);
      doc.text(empresa?.nome || "Casa de Repouso São Jorge", marginX + 20, 16);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(100, 116, 139);
      doc.text(`CNPJ: ${empresa?.cnpj || "-"}  ·  ${empresa?.telefone || ""}`, marginX + 20, 21);
      doc.setDrawColor(226, 232, 240); doc.line(marginX, 28, marginX + lineW, 28);
      doc.setTextColor(30, 41, 59);
      y = 36;
    } catch (e) { /* ignore */ }
  }

  const endContratante = [
    contratante.endLogradouro, contratante.endNumero, contratante.endBairro,
    contratante.endCidade, contratante.endUf,
  ].filter(Boolean).join(", ") + (contratante.endCep ? ` - CEP ${contratante.endCep}` : "");

  const write = (text, size = 10, bold = false, gap = 5) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, lineW);
    lines.forEach((ln) => {
      if (y > 280) { doc.addPage(); y = 20; }
      doc.text(ln, marginX, y);
      y += gap;
    });
  };

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("CONTRATO DE PRESTAÇÃO DE SERVIÇOS", 105, y, { align: "center" });
  y += 6;
  doc.setFontSize(11);
  doc.text("DE ACOLHIMENTO EM INSTITUIÇÃO DE LONGA PERMANÊNCIA", 105, y, { align: "center" });
  y += 10;

  write(`CONTRATADA: ${empresa?.nome || "Casa de Repouso São Jorge"}, inscrita no CNPJ sob o nº ${empresa?.cnpj || "-"}, doravante denominada CONTRATADA.`);
  y += 2;
  write(`CONTRATANTE (Responsável Financeiro): ${contratante.nome || "-"}, portador(a) do CPF nº ${contratante.cpf || "-"} e RG nº ${contratante.rg || "-"}, residente e domiciliado(a) em ${endContratante || "-"}, telefone ${contratante.telefone || "-"}, doravante denominado(a) CONTRATANTE.`);
  y += 2;
  write(`RESIDENTE (Beneficiário): ${residente.nome}, portador(a) do CPF nº ${residente.cpf || "-"}, acolhido(a) na unidade, ocupando o Quarto ${residente.quarto || "-"}.`);
  y += 4;

  write("CLÁUSULA 1ª – DO OBJETO", 10, true);
  write("O presente contrato tem por objeto a prestação de serviços de acolhimento, moradia, alimentação, higiene, cuidados e acompanhamento ao RESIDENTE acima qualificado, pela CONTRATADA, conforme normas da instituição e legislação vigente.");
  y += 2;

  write("CLÁUSULA 2ª – DO VALOR E DA MENSALIDADE", 10, true);
  write(`Pela prestação dos serviços, o(a) CONTRATANTE pagará à CONTRATADA a mensalidade no valor de ${brl(valorAtual)}, com vencimento mensal. O valor não inclui itens de consumo do estoque da instituição, que serão cobrados à parte na fatura mensal quando não houver fornecimento familiar disponível.`);
  y += 2;

  write("CLÁUSULA 3ª – DO REAJUSTE", 10, true);
  write("O valor da mensalidade será reajustado automaticamente na taxa de 8% (oito por cento) a cada 12 (doze) meses, contados a partir da data de início do contrato, renovando-se a vigência por igual período.");
  y += 2;

  write("CLÁUSULA 4ª – DA VIGÊNCIA", 10, true);
  write(`O contrato vigora a partir de ${dataBRfromISO(residente.dataInicioContrato)}, renovando-se automaticamente por períodos de 12 (doze) meses, salvo manifestação em contrário por qualquer das partes.`);
  y += 2;

  write("CLÁUSULA 5ª – DAS RESPONSABILIDADES", 10, true);
  write("O(A) CONTRATANTE é responsável financeiro pelo pagamento das mensalidades e faturas de consumo, bem como contato de emergência do RESIDENTE. A CONTRATADA compromete-se a prestar os serviços com zelo, respeitando a dignidade do RESIDENTE.");
  y += 6;

  write(`Local e data: ______________________________, ${dataBR(new Date())}.`, 10, false, 12);
  y += 10;

  if (y > 250) { doc.addPage(); y = 30; }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("_______________________________________", marginX, y);
  doc.text("_______________________________________", 110, y);
  y += 5;
  doc.text("CONTRATANTE", marginX, y);
  doc.text("CONTRATADA", 110, y);
  y += 5;
  doc.setFontSize(8);
  doc.text(contratante.nome || "", marginX, y);
  doc.text(empresa?.nome || "Casa de Repouso São Jorge", 110, y);

  doc.save(`contrato-${(residente.nome || "residente").replace(/\s+/g, "_")}.pdf`);
}
